// File: frontend/src/hooks/useApi.ts
import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

export const useApi = () => {
    const { instance, accounts } = useMsal();

    // --- Ottieni access token da MSAL ---
    const getAccessToken = useCallback(async () => {
        if (accounts.length === 0) throw new Error("No active account!");
        const request = { ...loginRequest, account: accounts[0] };
        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                const popupResponse = await instance.acquireTokenPopup(request);
                return popupResponse.accessToken;
            } else {
                throw error;
            }
        }
    }, [instance, accounts]);

    // --- Fetch autenticato con token ---
    const authenticatedFetcher = useCallback(async (endpoint: string) => {
        const token = await getAccessToken();
        const headers: HeadersInit = { 'Authorization': `Bearer ${token}` };
        const fullUrl = `${process.env.REACT_APP_API_BASE_URL}${endpoint}`;

        const response = await fetch(fullUrl, { headers });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Request failed with status ${response.status}`);
        }
        return response.json();
    }, [getAccessToken]);

    // --- Fetch semplice senza token ---
    const simpleTextFetcher = useCallback(async (url: string) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch content from ${url}`);
        return response.text();
    }, []);

    // --- Poster con token e JSON ---
    const poster = useCallback(
        async (endpoint: string, body: object, method: 'POST' | 'PUT' | 'DELETE' = 'POST') => {
            const token = await getAccessToken();
            const headers: HeadersInit = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            };

            const fullUrl = `${process.env.REACT_APP_API_BASE_URL}${endpoint}`;
            const response = await fetch(fullUrl, {
                method,
                headers,
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred.' }));
                throw new Error(errorData.detail);
            }

            return response.json();
        },
        [getAccessToken]
    );

    // --- Download autenticato di file binari/CSV (restituisce Blob) ---
    const authenticatedBlobFetcher = useCallback(async (endpoint: string) => {
        const token = await getAccessToken();
        const headers: HeadersInit = { 'Authorization': `Bearer ${token}` };
        const fullUrl = `${process.env.REACT_APP_API_BASE_URL}${endpoint}`;

        const response = await fetch(fullUrl, { headers });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Request failed with status ${response.status}`);
        }
        return response.blob();
    }, [getAccessToken]);

    return { authenticatedFetcher, simpleTextFetcher, poster, authenticatedBlobFetcher };
};
